// react
import React, { useMemo } from 'react';

// components
import DatePickerWrapper from './DatePickerWrapper/DatePickerWrapper';
import Skeletonize from '../Skeletons/Skeletonize';

// utils
import * as translator from '../utils/translator';

type Props = {
	onDateSelection: (date: string) => void;
	selectedDate: string | null;
	additionalClassName?: string;
	language?: string;
	isLoading?: boolean;
	activeDates?: string[];
}

const SelectDateCalanderPreferredTime: React.FC<Props> = ({ onDateSelection, selectedDate, additionalClassName, language="de", isLoading=false, activeDates }) => {
	// Calculate minimum date (next working day) - matches getNextWorkingDayVideoConsultation logic
	const minDate = useMemo(() => {
		const today = new Date();
		const dayOfWeek = today.getDay();
		const daysToAdd = dayOfWeek === 5 ? 3 : dayOfWeek === 6 ? 2 : 1; // Friday +3, Saturday +2, others +1
		const nextDate = new Date(today);
		nextDate.setDate(today.getDate() + daysToAdd);
        // Set to midnight so calendar dates compare correctly
		nextDate.setHours(0, 0, 0, 0); 
		return nextDate;
	}, []);

	const filterDate = (date: Date): boolean => {
		// If activeDates is provided, use those dates instead of working day filter
		if (activeDates && activeDates.length > 0) {
			// Use local timezone date formatting to match SelectDateCalander
			const year = date.getFullYear();
			const month = String(date.getMonth() + 1).padStart(2, '0');
			const day = String(date.getDate()).padStart(2, '0');
			const dateString = `${year}-${month}-${day}`;
			return activeDates.includes(dateString);
		}
		const dayOfWeek = date.getDay();
		return dayOfWeek !== 0 && dayOfWeek !== 6; // Exclude weekends
	};

	const getDayClassName = (date: Date): string | undefined => {
		// If activeDates is provided, check against those dates
		if (activeDates && activeDates.length > 0) {
			// Use local timezone date formatting to match SelectDateCalander
			const year = date.getFullYear();
			const month = String(date.getMonth() + 1).padStart(2, '0');
			const day = String(date.getDate()).padStart(2, '0');
			const dateString = `${year}-${month}-${day}`;
			
			if (!activeDates.includes(dateString)) {
				return "date-inactive pointer-events-none";
			}
			return "date-available";
		}
		
		// Otherwise use working day logic
		const dayOfWeek = date.getDay();
		if (date < minDate || dayOfWeek === 0 || dayOfWeek === 6) {
			return "date-inactive pointer-events-none";
		}
		return "date-available";
	}

	return (
		<div
			id="select-date-calander-preferred-time-container"
			className={`
        	    rounded-[16px]
				bg-[#ffffff]
				h-fit
				px-5 lg:px-8
				pt-4 lg:pt-9
				pb-4 lg:pb-9
				custom-scrollbar
				${additionalClassName}
			`}
		>
			<Skeletonize
				isLoading={isLoading}
				className='h-[24px] md:h-[26px] mb-[8px] md:mb-[6px]'
				animation='syncWave pulse'
			>
				<h3
					id="preferred-time-date-title"
					className="
						leading-[1.3]
						text-[21px] lg:text-[23px]  
						font-medium
						text-[#000000]
						capitalize
						pb-0
						animate-fade-in-up-10
					"
				>
					{translator.translate(language, "default", "schedule_video_consultation/select_date")}
				</h3>
			</Skeletonize>	

			<Skeletonize
				isLoading={isLoading}
				className='h-[15px] mb-[25px] md:mb-[40px]'
				animation='syncWave pulse'
			>
				<h5
					id="preferred-time-date-subtitle"
					className="
						font-normal
						capitalize
						font-roboto
						text-[#4B5B4B]
						text-[13px] md:text-[14px] lg:text-[15px]
						pb-0
						animate-fade-in-up-10
					"
				>
					{translator.translate(language, "default", "schedule_video_consultation/choose_your_preferred_appointment_date")}
				</h5>
			</Skeletonize>

			<div className="flex-1 flex items-center justify-center mt-[25px] md:mt-[40px] md:pb-8 relative z-10">
			<Skeletonize
					isLoading={isLoading}
					className='w-full aspect-square md:scale-150'
					animation='syncWave pulse'
				>
					<style>{`
						#select-date-calander-preferred-time-container .date-picker-appointment-wrapper .react-datepicker {
							border: none !important; padding: 0 !important;
						}
					`}</style>
					<DatePickerWrapper 
						onChange={onDateSelection}
						errorMode={false}
						value={selectedDate || ''}
						isBirthdayInput={false}
						inline={true}
						customDayClassName={getDayClassName}
						variant="appointment"
						minDate={minDate}
						filterDate={filterDate}
					/>
				</Skeletonize>
			</div>
		</div>
	);
}

export default SelectDateCalanderPreferredTime;

